import React, { useState, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet";
import { userService, roleService } from "/app/src/services";
import Controls from "./controls";
import Roles from "../roles";
import { Row, Col } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import Filter from "./filter";
import Status from "./status";
import { useTranslation } from "react-i18next";
import { User, Role as RoleType } from "/app/src/models";
import getOrderByQuery from "/app/src/helpers/table";
import { useQuery } from "@tanstack/react-query";
import { Tag } from "/app/src/components/HeroUi/Chip";
import { buildParams } from "/app/src/helpers";
import { useAuthState } from "/app/src/contexts/authentication";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "/app/src/components/generic/tables/table";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import { IconToolTip } from "/app/src/components/icons/IconBuilder";
import useQueryParams from "/app/src/hooks/useQueryParams";

type query = {
  limit: number;
  page: number;
  status?: string;
  roleId?: number | string;
  countOnly?: boolean;
  orderby?: string;
};

/**
 * A component that displays a list of users with filtering, sorting, and pagination functionalities.
 * @returns {JSX.Element} The rendered UserList component.
 */
export default function UserList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { authType } = useAuthState();
  const [sort, setSort] = useSortUpgrade();
  const [selectedRole, setSelectedRole] = useState<string | number>("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(
    parseInt(searchParams.get("page") || "1") - 1,
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get("pageSize") || "25"),
  );

  //Get users
  const { data: users, isFetching } = useQuery({
    queryKey: ["users", page, pageSize, selectedRole, selectedStatus, sort],
    queryFn: () => {
      const query: query = {
        limit: pageSize,
        page,
        orderby: getOrderByQuery(sort),
      };
      if (selectedRole !== "all") {
        query["roleId"] = selectedRole;
      }
      if (selectedStatus !== "all") {
        query["status"] = selectedStatus;
      }
      return userService.getAll(buildParams(query));
    },
    initialData: { users: [] },
    select: (data: { users: User[] }) => {
      return data.users;
    },
  });

  //Get number of users
  const { data: usersCount } = useQuery({
    queryKey: ["usersCount", selectedRole, selectedStatus],
    queryFn: () => {
      const query = {};
      if (selectedRole !== "all") {
        query["roleId"] = selectedRole;
      }
      if (selectedStatus !== "all") {
        query["status"] = selectedStatus;
      }
      return userService.getCount(buildParams(query));
    },
    initialData: { count: 0 },
    select: (data: { count: number }) => {
      return data.count;
    },
  });

  const { updatePage, updatePageSize } = useQueryParams({
    count: usersCount,
    isFetching,
    page,
    setPage,
    pageSize,
    setPageSize,
  });

  const tableHelper = createColumnHelper<User>();
  const columns = useMemo(
    () => [
      tableHelper.accessor("username", {
        id: "username",
        cell: (info) => info.getValue(),
        header: t("translation:username"),
      }),
      tableHelper.accessor("email", {
        id: "email",
        cell: (info) => info.getValue(),
        header: t("translation:email"),
      }),
      tableHelper.accessor("status", {
        id: "status",
        cell: (info) => {
          const statusValue = Status(info.getValue());
          return (
            <div className="flex items-center">
              {statusValue}
              {statusValue === "Pending" && (
                <IconToolTip
                  className="ml-1"
                  content={t("translation:status_info")}
                />
              )}
            </div>
          );
        },
        header: t("translation:status"),
      }),
      tableHelper.accessor("type", {
        id: "type",
        cell: (info) => {
          return <p>{info.getValue()}</p>;
        },
        header: t("translation:type"),
      }),
      tableHelper.accessor("roleName", {
        id: "roleName",
        cell: (info) => {
          return (
            <Tag className={info.getValue() === null ? "h-0" : ""}>
              {info.getValue()}
            </Tag>
          );
        },
        header: t("translation:role"),
      }),
    ],
    [tableHelper, t],
  );

  /**
   * Function to navigate to user details page.
   * @param {number} userId The unique identifier of the user.
   */
  const goToUser = useCallback(
    (userId: number) => {
      navigate(`/users/${userId}`);
    },
    [navigate],
  );

  //Get roles
  const { data: roles } = useQuery({
    queryKey: ["roles"],
    queryFn: () => {
      return roleService.getAll();
    },
    initialData: { roles: [] },
    select: (data: { roles: RoleType[] }) => {
      return data.roles;
    },
  });
  return (
    <div className="users">
      <Helmet>
        <title>{t("translation:users")} - ItemPath</title>
      </Helmet>
      {authType === "basic" && <Controls />}
      <Row gutter={20}>
        <Col span={6}>
          <Filter
            roles={roles}
            setRole={setSelectedRole}
            setStatus={setSelectedStatus}
          />
          <Roles roles={roles} />
        </Col>
        <Col span={18}>
          <Table
            loading={isFetching}
            rows={users}
            tableColumns={columns}
            length={usersCount}
            sort={sort}
            setSort={setSort}
            paginationEnabled={{
              currentPage: page,
              pageSize,
              setPage: updatePage,
              setPageSize: updatePageSize,
            }}
            enableRowSelection
            rowClicked={goToUser}
            emptyText="No Users Found"
          />
        </Col>
      </Row>
    </div>
  );
}
